import React, { useState, useEffect } from 'react';
import {
  EXAM_TITLES,
  COLOR_ENABLED,
  COLOR_DISABLED,
  COLOR_TEXT_INFO
} from '../constants';
import Link from '@/presentation/reusables/Link';
import { Block } from '@/presentation/components';
import TextHelper from '@/core/helpers/TextHelper';
import DateHelpers from '@/core/helpers/DateHelper';
import { ExamViewMobile } from './Mobile/ExamViewMobile';
import { ISectionExamRolProps, IExamsRol } from './types';
import useIsMobile from '@/presentation/hooks/useIsMobile';
import { ExamsViewDesktop } from './Desktop/ExamsViewDesktop';
import SectionHeader from '@/presentation/reusables/SectionHeader';
import Collapsible from '@/presentation/modules/courses/modules/myCourses/components/MyCoursesCollapsibles/Collapsible';

const SectionExamRol = ({ dataExamRol = {} }: ISectionExamRolProps) => {
  const [viewExams, setViewExams] = useState<Record<string, boolean>>({});
  const [firtDateExam, setFirtDateExam] = useState<number | null>(null);
  const keyExamsFinals = Object.keys(EXAM_TITLES)[0];
  const keyExamsReturn = Object.keys(EXAM_TITLES)[2];
  const isMobile = useIsMobile(true);

  const isExamsReturn = (key: string) => key === keyExamsReturn;

  const textDescription = (key: string) => {
    if (key === keyExamsFinals) {
      return (
        <>
          También puedes ver la programación de tus exámenes en{' '}
          <Link
            linkType="internal"
            url={`/Calendario?fecha=${DateHelpers.longUTCToFormatYMD(
              firtDateExam ?? 0
            )}`}
            linkName="Calendario."
          />
        </>
      );
    }
    if (key === keyExamsReturn) {
      return 'Consulta cuándo puedes recoger y revisar tu examen final.';
    }
    return '';
  };

  const typeExamByCourse = (course: IExamsRol) => {
    if (dataExamRol.examsFinals?.includes(course)) {
      return 'Examen final';
    }
    if (dataExamRol.examsLagging?.includes(course)) {
      return 'Examen rezagado';
    }
    if (dataExamRol.examsReturn?.includes(course)) {
      return 'Devolución de examen';
    }
    return '';
  };

  const formatDateExam = (date: number, isTitle?: boolean) => {
    const formattedDate = DateHelpers.longUTCToFormatYMD(date);
    const parts = formattedDate.split('-').map(Number);
    if (parts?.length !== 3) return '';

    const [year, month, day] = parts;
    const dateObj = new Date(year, month - 1, day);
    const formatted = isTitle
      ? DateHelpers.formatFullDayAndMonth(dateObj)
      : DateHelpers.formatFullDate2(dateObj);
    return TextHelper.capitalize(formatted, false, true);
  };

  const handlerOpenCollapsibleNextExam = (
    allExamsValidateNext: IExamsRol[]
  ) => {
    // exam with next date ordened and one or more courses enabled
    const firstExam = allExamsValidateNext.reduce<IExamsRol | null>(
      (prev, current) => {
        const prevEnabled = prev?.courses?.some((c) => !c.isDisabled) ?? false;
        const currentEnabled =
          current?.courses?.some((c) => !c.isDisabled) ?? false;
        if (!prev && currentEnabled) return current;
        if (!currentEnabled) return prev;

        return prevEnabled && new Date(current.date) < new Date(prev!.date)
          ? current
          : prev;
      },
      null
    );
    if (!firstExam) return;

    // get keyExam by firstExam
    const keyExam = Object.keys(dataExamRol).find((key) =>
      dataExamRol[key].includes(firstExam)
    );
    if (!keyExam) return;
    setViewExams((prev: Record<string, boolean>) => ({
      ...prev,
      [`${keyExam}-${firstExam.id}`]: true
    }));
  };

  const toggleExamView = (key: string, examId: string | number) => {
    setViewExams((prev) => ({
      ...prev,
      [`${key}-${examId}`]: !prev[`${key}-${examId}`]
    }));
  };

  useEffect(() => {
    const allExamsValidateNext = [
      ...(dataExamRol?.examsFinals ?? []),
      ...(dataExamRol?.examsLagging ?? [])
    ];
    if (allExamsValidateNext?.length === 0) return;
    setFirtDateExam(dataExamRol?.examsFinals?.[0]?.date ?? null);
    handlerOpenCollapsibleNextExam(allExamsValidateNext);
  }, []);

  return (
    <>
      {Object.entries(dataExamRol)
        ?.filter(
          ([, exams]) =>
            Array.isArray(exams) &&
            exams?.length > 0 &&
            exams.some((e) => e?.courses?.length > 0)
        )
        ?.map(([key, exams]) => (
          <Block
            key={key}
            data-testid={`cmp-section-exam-rol-${key}`}
            style={{
              backgroundColor: '#D5E5F0',
              borderRadius: '8px',
              padding: '16px'
            }}
          >
            <SectionHeader
              title={EXAM_TITLES[key] ?? 'Exámenes'}
              style={{ padding: 0, marginBottom: '12px' }}
              styleTitle={{ fontSize: '20px', lineHeight: '32px' }}
              description={textDescription(key)}
            />
            {exams?.map((exam: IExamsRol) => (
              <Collapsible
                key={exam.id}
                courseStatus=""
                catalogNumber=""
                color={
                  exam?.courses?.some((course) => !course.isDisabled)
                    ? COLOR_ENABLED
                    : COLOR_DISABLED
                }
                show={viewExams[`${key}-${exam.id}`] || isExamsReturn(key)}
                setShow={() => toggleExamView(key, exam.id)}
                alwaysOpen={isExamsReturn(key)}
                title={formatDateExam(exam.date, true)}
                styleTitle={{
                  fontWeight: 700,
                  paddingTop: '10px',
                  color: exam?.courses?.some((course) => !course.isDisabled)
                    ? COLOR_TEXT_INFO
                    : COLOR_DISABLED
                }}
                // styleChildren={{ padding: '0px 12px' }}
              >
                {isMobile ? (
                  exam.courses?.map((course) => (
                    <ExamViewMobile
                      key={course.id}
                      course={course}
                      dateExam={formatDateExam(exam.date)}
                      typeExam={typeExamByCourse(exam)}
                    />
                  ))
                ) : (
                  <ExamsViewDesktop
                    dateExam={formatDateExam(exam.date)}
                    coursesData={exam.courses}
                    typeExam={typeExamByCourse(exam)}
                  />
                )}
              </Collapsible>
            ))}
          </Block>
        ))}
    </>
  );
};

export default SectionExamRol;
